import { Button } from '@mui/material'
import { forwardRef } from 'react'
import { TextInputField, TextInputFieldProps } from 'utoniq-ui'

import { tLabel, useMessage, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { Form, useForm, UseFormProps } from '@/core/form'

export type SMSVerificationFormProps = {
  loading: boolean
  onSubmit: (inputs: SMSVerificationFormValues) => Promise<void>
  onResend: () => Promise<void>
}

export type SMSVerificationFormValues = {
  code: string
}

const schema: UseFormProps['schema'] = o => ({
  code: o.string().length(6).label(tLabel('smsVerification.code')),
})

export const SMSVerificationForm: Organism<SMSVerificationFormProps> = ({
  loading,
  onSubmit,
  onResend,
}) => {
  const { t } = useTranslation('smsVerification')
  const { showMessage, showError } = useMessage()

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting, isValid },
  } = useForm<SMSVerificationFormValues>({
    schema,
  })

  const handleVerify = handleSubmit(values => {
    return onSubmit(values).catch(showError)
  })

  const handleReSend = () =>
    onResend()
      .then(() => showMessage(t('resended')))
      .catch(showError)

  return (
    <Form onSubmit={handleVerify}>
      <TextInputCell type="text" error={errors?.code} {...register('code')} />
      <ButtonCell loading={isSubmitting} disabled={!isValid || isSubmitting} />
      <ResendButtonCell onResendAuthMail={handleReSend} loading={loading} />
    </Form>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('smsVerification')
    return (
      <CellFrame>
        <TextInputField
          {...props}
          ref={ref}
          t={t}
          titleProps={{ sx: { display: 'none' } }}
        />
      </CellFrame>
    )
  }
)

type ButtonCellProps = {
  loading: boolean
  disabled: boolean
}
const ButtonCell: Cell<ButtonCellProps> = props => {
  const { t } = useTranslation('smsVerification')

  return (
    <CellFrame sx={{ py: 2, textAlign: 'center' }}>
      <Button type="submit" size="large" sx={{ width: 244 }} {...props}>
        {t('submit')}
      </Button>
    </CellFrame>
  )
}

const ResendButtonCell: Cell<{
  onResendAuthMail: () => void
  loading: boolean
}> = ({ onResendAuthMail, loading }) => {
  const { t } = useTranslation('smsVerification')
  return (
    <CellFrame sx={{ textAlign: 'center' }}>
      <Button
        variant="text"
        color="primary"
        onClick={onResendAuthMail}
        disabled={loading}
      >
        {t('resend')}
      </Button>
    </CellFrame>
  )
}
